import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Loader2, Shield } from "lucide-react";
import { toast } from "sonner";
import { useOutletContext } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { adminApi } from "./adminApi";
import { Field } from "./components";

const ROLES = [
  { v: "super_admin", l: "Super Admin" },
  { v: "content_manager", l: "Content Manager" },
  { v: "event_operator", l: "Event Operator" },
];

export default function UsersAdmin() {
  const { user } = useOutletContext();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["admin-users"], queryFn: adminApi.listUsers });
  const [adding, setAdding] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-users"] });
  const list = data || [];

  const remove = async (u) => { if (window.confirm(`Delete ${u.email}?`)) { try { await adminApi.deleteUser(u.id); toast.success("Deleted"); refresh(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } } };
  const changeRole = async (u, role) => { await adminApi.updateUser(u.id, { role }); toast.success("Role updated"); refresh(); };

  return (
    <div data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <div><h1 className="font-head text-3xl font-extrabold text-white">Admin Users</h1><p className="mt-1 text-slate-400">Manage access and roles.</p></div>
        <Button onClick={() => setAdding(true)} className="cyan-grad text-[#041021] font-bold" data-testid="user-add-btn"><Plus className="h-4 w-4" /> New User</Button>
      </div>
      {isLoading ? <Loader2 className="mt-10 h-8 w-8 animate-spin text-cyan-400" /> : (
        <div className="mt-6 space-y-3">
          {list.map((u) => (
            <div key={u.id} className="flex items-center gap-4 rounded-2xl glass p-4" data-testid={`user-row-${u.email}`}>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-cyan-400/40 text-cyan-300"><Shield className="h-5 w-5" /></div>
              <div className="min-w-0 flex-1"><p className="truncate font-medium text-white">{u.name}</p><p className="truncate text-sm text-slate-400">{u.email}</p></div>
              <select value={u.role} disabled={u.id === user.id} onChange={(e) => changeRole(u, e.target.value)}
                className="rounded-lg border border-slate-700 bg-[#0b1730] px-3 py-2 text-sm text-white disabled:opacity-50" data-testid={`user-role-${u.email}`}>
                {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
              </select>
              <button onClick={() => remove(u)} disabled={u.id === user.id} className="rounded-lg p-2 text-slate-400 hover:text-rose-300 disabled:opacity-30"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}
      {adding && <UserEditor onClose={() => setAdding(false)} onSaved={() => { setAdding(false); refresh(); }} />}
    </div>
  );
}

function UserEditor({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "content_manager" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.email || !form.password || form.password.length < 6) { toast.error("Email and password (min 6 chars) required"); return; }
    setSaving(true);
    try { await adminApi.createUser(form); toast.success("User created"); onSaved(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-[#0b1730] border-cyan-500/20 text-white" data-testid="user-editor">
        <DialogHeader><DialogTitle className="font-head">New Admin User</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-[#050b18] border-slate-700" data-testid="user-name" /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#050b18] border-slate-700" data-testid="user-email" /></Field>
          <Field label="Password"><Input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} className="bg-[#050b18] border-slate-700" data-testid="user-password" /></Field>
          <Field label="Role">
            <select value={form.role} onChange={(e) => set("role", e.target.value)} className="w-full rounded-lg border border-slate-700 bg-[#050b18] px-3 py-2 text-sm" data-testid="user-role-select">
              {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </Field>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button className="cyan-grad text-[#041021] font-bold" disabled={saving} onClick={save} data-testid="user-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
